// WebView ↔ Native 메시지 프로토콜
// 양쪽(apps/web, apps/native)이 import하여 타입 일치 보장.
// 런타임 검증은 dispatcher의 가벼운 typeguard + try/catch로 처리 (zod 미사용).

// 2 — `version.fetch` 추가(구버전 앱은 dispatcher default 분기가 ok:false 로 답한다 → 호출측 fail-open).
// 3 — `systemUi.setTheme` 추가(단방향 통지라 응답 없음 — 구버전 앱은 default 분기에서 조용히 무시,
//     상태바만 예전처럼 OS 를 따른다 → 기능 저하일 뿐 깨지지 않는다).
export const BRIDGE_VERSION = 3;

// ---------------------------------------------------------------------------
// Web → Native
// ---------------------------------------------------------------------------

export type WebToNative =
  | { type: 'app.ready' }
  | { type: 'secure.get'; reqId: string; payload: { key: string } }
  | { type: 'secure.set'; reqId: string; payload: { key: string; value: string } }
  | { type: 'secure.delete'; reqId: string; payload: { key: string } }
  | { type: 'push.getFcmToken'; reqId: string }
  | { type: 'push.requestPermission'; reqId: string }
  | { type: 'haptics.impact'; payload: { style: 'light' | 'medium' | 'heavy' } }
  // 외부 URL 열기. `mode` 로 표면 선택:
  //   - `in-app`(기본) — SFSafariViewController / Chrome Custom Tabs. 앱 위에 얹혀 닫으면 바로 복귀.
  //   - `system`       — 진짜 브라우저 앱으로 **완전히 이탈**(Linking.openURL). 복귀는 사용자 몫(딥링크 필요).
  | { type: 'app.openExternal'; payload: { url: string; mode?: 'in-app' | 'system' } }
  | {
      type: 'analytics.event';
      payload: { name: string; params: Record<string, unknown> };
    }
  // 배포 도메인의 정적 버전 매니페스트(`/version.json`)를 **native 네트워크 스택으로** 가져온다.
  //
  // **왜 native 가 대신 받나** — WebView origin 은 `app://app.heviton.local`(iOS) /
  // `https://app.heviton.local`(Android) 이라 배포 도메인(`app.heviton.com` 등)은 cross-origin 이다.
  // 정적 호스트가 `Access-Control-Allow-Origin` 을 주지 않으면 WebView 의 fetch 는 CORS 로 차단되고,
  // `mode:'no-cors'` 는 opaque 응답이라 본문을 읽을 수 없다. native fetch 는 CORS 주체가 아니라 통과한다.
  //
  // URL 은 **web 이 소유**한다(env `deployBaseUrl` 이 SSOT — native 는 배포 도메인을 모른다).
  // native 는 스킴만 검증(https 전용)하고 GET 한 뒤 **본문 문자열을 그대로** 돌려준다 — JSON 파싱·형태
  // 검증은 매니페스트 타입을 아는 web 몫.
  | { type: 'version.fetch'; reqId: string; payload: { url: string } }
  // 현재 적용 테마를 native 셸에 알린다 — **테마의 주인은 web**(localStorage 선택 + `.dark` 클래스)이라
  // native 는 스스로 알 방법이 없다. native 는 이 값으로 상태바 글자색과 시스템 배경(세이프에어리어·
  // 오버스크롤 영역)을 맞춘다.
  //
  // 보내는 값은 **해석된 결과**(`light` | `dark`) — 사용자가 고른 모드(`system` 포함)가 아니다.
  // OS 다크모드가 바뀌어 `system` 의 해석이 뒤집힐 때도 web 이 다시 보낸다(판정 주체를 한쪽으로 몰아둔다).
  | { type: 'systemUi.setTheme'; payload: { theme: 'light' | 'dark' } };

// ---------------------------------------------------------------------------
// Native → Web
// ---------------------------------------------------------------------------

export type NativeToWeb =
  | { type: 'response'; reqId: string; ok: true; data: unknown }
  | { type: 'response'; reqId: string; ok: false; error: string }
  | { type: 'app.background' }
  | { type: 'app.foreground' }
  | { type: 'push.received'; payload: { data: Record<string, string> } }
  // FCM 토큰 발급/갱신을 native 가 web 으로 push (부팅 시 권한 획득 후 + onTokenRefresh).
  // web 은 로그인 body(`session.app.fcmToken`)에 실어 BE 전송. reqId 없는 비요청 이벤트.
  | { type: 'push.tokenIssued'; payload: { token: string } }
  // 사용자가 알림을 탭해 앱이 열림(background/quit). native 는 RemoteMessage.data 원본만 전달하고
  // 코드→라우트 매핑·네비게이션은 web(라우팅 SSOT)이 담당.
  | { type: 'push.notificationOpened'; payload: { data: Record<string, string> } }
  // 커스텀 스킴(`heviton://<path>`)으로 앱이 열림 — 실행 중 수신 + quit-state 최초 URL 양쪽. native 는
  // **URL 원문만** 넘기고 경로 해석·네비게이션은 web(라우팅 SSOT)이 한다(`push.notificationOpened` 와 동형).
  | { type: 'deeplink'; payload: { url: string } }
  // iOS 상태바 탭(최상단 이동 시스템 제스처). 실제 스크롤러는 WKWebView 가 아니라 web 내부 스크롤 영역이라
  // native 는 제스처만 위임하고 **스크롤은 web 이 수행**한다. Android 는 대응 제스처가 없어 미발화.
  | { type: 'app.scrollToTop' }
  | { type: 'network.change'; payload: { online: boolean } };

// ---------------------------------------------------------------------------
// __NATIVE__ injection (네이티브가 WebView 컨텐트 로드 전 주입)
// ---------------------------------------------------------------------------

export interface NativeContext {
  platform: 'ios' | 'android';
  appVersion: string;
  buildNumber?: string;
  bridgeVersion: number;
  /**
   * EAS Update 채널(`development` | `preview` | `production`). 빌드 프로필의 `channel` 이 그대로 박힌다.
   * **preview 빌드를 식별하는 유일한 런타임 신호** — preview 프로필은 `environment: production` 을 써서
   * env 변수·번들 ID·웹 빌드 mode 가 production 과 동일하고, 오직 channel 만 다르다.
   * dev client / Expo Go 처럼 EAS Update 미구성 환경은 undefined.
   */
  updateChannel?: string;
  /** 디바이스(앱 설치 인스턴스) 고유 식별자. 첫 부팅 시 native 가 생성·secure store 영속. */
  terminalId: string;
}

// ---------------------------------------------------------------------------
// Secure store keys — web zustand persist 와 native cleanup 양쪽이 SSOT 로 의존.
// ---------------------------------------------------------------------------

export const SecureKeys = {
  /** web 의 useAuthStore zustand persist name. native fresh-install cleanup 대상. */
  auth: 'heviton.auth',
  /** terminalId 보관 키. native 만 직접 read/write (web 은 NativeContext 로 받음). */
  terminalId: 'heviton.terminal-id',
} as const;

// ---------------------------------------------------------------------------
// 가벼운 런타임 가드 — dispatcher 진입점에서 사용
// 양쪽이 우리 통제 하의 코드라 신뢰 가정. 모양 점검만 하여 default 분기로 안전 처리.
// ---------------------------------------------------------------------------

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export function isWebToNative(v: unknown): v is WebToNative {
  return isPlainObject(v) && typeof v.type === 'string';
}

export function isNativeToWeb(v: unknown): v is NativeToWeb {
  return isPlainObject(v) && typeof v.type === 'string';
}
